/**
 * Path-driven breadcrumb для AppShell-топбара. Translates каждый сегмент
 * через `nav.<segment>`, fallback — сырой сегмент.
 *
 * Спец-кейс `/projects/<id>` (§4.1 task 13): вместо «Главная / Проекты / 6»
 * показываем компактную шапку проекта `Проекты / <CODE> | <NAME> [status]`,
 * чтобы максимально освободить вертикаль страницы под Гант. Project тянется
 * через `useProject` только когда мы на этом маршруте — на остальных
 * экранах хук не вызывается.
 */
import { useTranslation } from "react-i18next";
import { Link, useRouterState } from "@tanstack/react-router";

import { useProject } from "@/hooks/useProjects";
import { usePerson } from "@/hooks/usePeople";
import { useUnit } from "@/hooks/useUnits";
import { useSession } from "@/store/session";

export function Breadcrumb(): React.ReactElement {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const role = useSession((s) => s.user?.role);
  const parts = pathname.split("/").filter(Boolean);

  if (parts[0] === "admin" && parts[1] === "catalogs" && role !== "admin") {
    return <PathBreadcrumb parts={["catalogs"]} />;
  }

  const projectIdRaw = parts[0] === "projects" && parts.length === 2 ? parts[1] : null;
  const projectId =
    projectIdRaw !== null && /^\d+$/.test(projectIdRaw) ? Number(projectIdRaw) : null;
  if (projectId !== null) {
    return <ProjectBreadcrumb projectId={projectId} />;
  }

  // `/people/<id>` — show the person's name, not the technical id (the trailing
  // segment would otherwise warn + render «—» in the generic breadcrumb).
  const personIdRaw = parts[0] === "people" && parts.length === 2 ? parts[1] : null;
  const personId = personIdRaw !== null && /^\d+$/.test(personIdRaw) ? Number(personIdRaw) : null;
  if (personId !== null) {
    return <PersonBreadcrumb personId={personId} />;
  }

  const unitIdRaw = parts[0] === "units" && parts.length === 2 ? parts[1] : null;
  const unitId = unitIdRaw !== null && /^\d+$/.test(unitIdRaw) ? Number(unitIdRaw) : null;
  if (unitId !== null) {
    return <UnitBreadcrumb unitId={unitId} />;
  }

  return (
    <PathBreadcrumb
      parts={parts}
      finalLabelKey={pathname === "/projects/new" ? "projects.new.title" : undefined}
    />
  );
}

function UnitBreadcrumb({ unitId }: { unitId: number }): React.ReactElement {
  const { t } = useTranslation();
  const { data } = useUnit(unitId);
  return (
    <nav
      aria-label={t("nav.breadcrumb")}
      className="text-muted-foreground flex items-center gap-1 text-sm"
      data-testid="topbar-unit-crumb"
    >
      <Link to="/home" className="hover:text-foreground">
        {t("nav.home")}
      </Link>
      <span aria-hidden="true">/</span>
      <Link to="/people" search={{ tab: "structure" }} className="hover:text-foreground">
        {t("nav.users")}
      </Link>
      <span aria-hidden="true">/</span>
      <span className="text-foreground font-medium">{data?.name ?? "…"}</span>
    </nav>
  );
}

function PersonBreadcrumb({ personId }: { personId: number }): React.ReactElement {
  const { t } = useTranslation();
  const { data } = usePerson(personId);
  return (
    <nav
      aria-label={t("nav.breadcrumb")}
      className="text-muted-foreground flex items-center gap-1 text-sm"
      data-testid="topbar-person-crumb"
    >
      <Link to="/home" className="hover:text-foreground">
        {t("nav.home")}
      </Link>
      <span aria-hidden="true">/</span>
      <Link to="/people" search={{ tab: "employees" }} className="hover:text-foreground">
        {t("nav.users")}
      </Link>
      {data?.profile.unit_id != null && data.profile.unit_name != null && (
        <>
          <span aria-hidden="true">/</span>
          <Link
            to="/units/$unitId"
            params={{ unitId: String(data.profile.unit_id) }}
            className="hover:text-foreground"
          >
            {data.profile.unit_name}
          </Link>
        </>
      )}
      <span aria-hidden="true">/</span>
      <span className="text-foreground font-medium">{data?.profile.full_name ?? "…"}</span>
    </nav>
  );
}

function PathBreadcrumb({
  parts,
  finalLabelKey,
}: {
  parts: string[];
  finalLabelKey?: "projects.new.title";
}): React.ReactElement {
  const { t } = useTranslation();
  const accumulated: string[] = [];
  return (
    <nav
      aria-label={t("nav.breadcrumb")}
      className="text-muted-foreground flex items-center gap-1 text-sm"
    >
      <Link to="/home" className="hover:text-foreground">
        {t("nav.home")}
      </Link>
      {parts
        .filter((p) => p !== "home")
        .map((part, i, arr) => {
          accumulated.push(part);
          const isLast = i === arr.length - 1;
          const prev = i > 0 ? arr[i - 1] : "";
          // Если для slug'а нет ключа `nav.<slug>` — «—» + warn в консоль
          // (а не сырой kebab-case), чтобы slug не уехал в UI.
          // см. правило «нет английского / технических slug'ов в UI» в
          // `frontend/CLAUDE.md`.
          let label: string;
          if (isLast && finalLabelKey) {
            label = t(finalLabelKey);
          } else if (prev === "approvals") {
            label = t("nav.approvalDetail");
          } else {
            const translated = t(`nav.${part}`, { defaultValue: "" });
            if (translated === "") {
              console.warn("Missing nav i18n key:", part);
              label = "—";
            } else {
              label = translated;
            }
          }
          return (
            <span key={accumulated.join("/")} className="flex items-center gap-1">
              <span>/</span>
              {isLast ? (
                <span className="text-foreground font-medium">{label}</span>
              ) : (
                <Link to={"/" + accumulated.join("/")} className="hover:text-foreground">
                  {label}
                </Link>
              )}
            </span>
          );
        })}
    </nav>
  );
}

function ProjectBreadcrumb({ projectId }: { projectId: number }): React.ReactElement {
  const { t } = useTranslation();
  const { data: project } = useProject(projectId);
  return (
    <nav
      aria-label={t("nav.breadcrumb")}
      className="flex items-center gap-2 text-sm"
      data-testid="topbar-project-crumb"
    >
      <Link to="/home" className="text-muted-foreground hover:text-foreground">
        {t("nav.home")}
      </Link>
      <span className="text-muted-foreground" aria-hidden="true">
        /
      </span>
      <Link to="/projects" className="text-muted-foreground hover:text-foreground">
        {t("projects.title")}
      </Link>
      <span className="text-muted-foreground" aria-hidden="true">
        /
      </span>
      {project?.code ? (
        <span className="text-muted-foreground" data-testid="topbar-project-code">
          {project.code}
        </span>
      ) : (
        <span className="text-muted-foreground">…</span>
      )}
      {project && (
        <>
          <span
            aria-hidden="true"
            className="mx-1 inline-block h-5 w-px"
            style={{ background: "var(--rule-line)" }}
          />
          <span
            className="text-foreground font-semibold"
            style={{ fontSize: "18px", lineHeight: 1.2 }}
            data-testid="topbar-project-name"
          >
            {project.name}
          </span>
        </>
      )}
    </nav>
  );
}
